import type { FC } from 'react'
import styled from '@emotion/styled'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import NavigationV2 from '@segment/templates/Navigation2'
import Footer from '@segment/templates/Footer'

import { useLessonQuery, useCoursesQuery } from '@segment/api/routes/academy/queries'
import type { Course } from '@segment/api/routes/academy/types'

import Layout from '@segment/next-shared/layout'

import Subnav from '../components/Subnav'

import Hero from './components/Hero'
import Content from './components/Content'
import NextLesson from './components/NextLesson'
import WhatsNext from './components/WhatsNext'
import FaqSection from './components/FaqSection'

interface AcademyLessonViewProps {
  courseSlug: string
  lessonSlug: string
}

const AcademyLessonView: FC<AcademyLessonViewProps> = ({ courseSlug, lessonSlug }) => {
  const { data } = useLessonQuery({ args: [{ courseSlug, lessonSlug }] })
  const { data: _courses } = useCoursesQuery({ args: [] })

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout pageTitle={data.courseTitle} pageDescription={data.description} pageOG={data.image?.src}>
        <NavigationV2
          after={
            _courses && (
              <Subnav
                data={_courses?.map((course: Course) => ({
                  title: course.title,
                  courseNumber: course.courseNumber,
                  slug: course.slug,
                  lessons: course.lessons.map((lesson) => ({
                    title: lesson.title,
                    slug: lesson.slug,
                  })),
                }))}
              />
            )
          }
        />
        <Hero
          title={data.title}
          description={data.description}
          courseNumber={data.courseNumber}
          lessonNumber={data.lessonNumber}
        />
        <Main>
          <Content data={data.content} />
          {data.faqs && <FaqSection data={data.faqs} />}
          {data.nextLesson ? (
            <NextLesson data={data.nextLesson} courseNumber={data.courseNumber} courseIcon={data.courseIcon} />
          ) : (
            <WhatsNext />
          )}
        </Main>
        <Footer />
      </Layout>
    </AppearanceProvider>
  )
}

const Main = styled.main``

export default AcademyLessonView
